import React, { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import "./index.css";
import ExternalLink from "./ExternalLink";
import WritingArticle from "./WritingArticle";
import WritingTableOfContents from "./WritingTableOfContents";
import { ChevronLeft } from "lucide-react";
import { writings } from "./writings";
import type { Writing } from "./writings";
import { getHeadingIds, getWritingToc } from "./writingUtils";

type View = "about" | "writings" | "writing";

const writingPath = (slug: string) => `/writing/${slug}`;

const getWritingFromPath = (pathname: string): Writing | undefined => {
  const match = pathname.match(/^\/writing\/([^/]+)\/?$/);
  if (!match) return undefined;
  return writings.find((writing) => writing.slug === decodeURIComponent(match[1]));
};

const startViewTransition = (updater: () => void, options?: { disablePinnedHeader?: boolean }) => {
  const doc = document as Document & {
    startViewTransition?: (callback: () => void) => { finished?: Promise<unknown> };
  };
  if (typeof doc.startViewTransition !== "function") {
    updater();
    return;
  }
  if (options?.disablePinnedHeader) {
    document.documentElement.classList.add("disable-pinned-header-transition");
  }
  const transition = doc.startViewTransition(() => flushSync(updater));
  transition.finished?.finally(() => {
    document.documentElement.classList.remove("disable-pinned-header-transition");
  });
};

const App: React.FC = () => {
  const initialWriting = getWritingFromPath(window.location.pathname);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [view, setView] = useState<View>(initialWriting ? "writing" : "about");
  const [selectedWritingSlug, setSelectedWritingSlug] = useState(initialWriting?.slug ?? writings[0].slug);
  const selectedWriting = writings.find((writing) => writing.slug === selectedWritingSlug) ?? writings[0];
  const selectedWritingBody = useMemo(
    () =>
      selectedWriting.body ??
      selectedWriting.placeholder.map((paragraph: string, index: number) => ({
        type: "paragraph" as const,
        text: paragraph,
        bold: selectedWriting.boldLastParagraph && index === selectedWriting.placeholder.length - 1,
      })),
    [selectedWriting]
  );
  const writingToc = useMemo(() => {
    const toc = getWritingToc(selectedWritingBody);
    if (toc.length === 0) return [];
    return [{ id: "writing-title", text: selectedWriting.title, depth: 2 as const }, ...toc];
  }, [selectedWritingBody, selectedWriting.title]);
  const headingIds = useMemo(() => getHeadingIds(selectedWritingBody), [selectedWritingBody]);
  const themeToggleClassName =
    "theme-toggle relative h-5 w-3 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-secondary dark:text-primary";
  const wipTagClassName =
    "rounded-[3px] bg-badge px-1.5 py-px text-[10px] font-medium uppercase leading-4 text-badge-foreground";

  useEffect(() => {
    // Manage scroll position ourselves; browser scroll restoration can leave the
    // writing view computing its active section at a stale scroll offset, which
    // hides every other section and shows a blank screen (notably on mobile).
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.classList.add("initial-load-complete");
    }, 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.classList.toggle("writing-route", view === "writing");
    return () => document.documentElement.classList.remove("writing-route");
  }, [view]);

  useEffect(() => {
    const handlePopState = () => {
      const writing = getWritingFromPath(window.location.pathname);
      startViewTransition(() => {
        if (writing) {
          setSelectedWritingSlug(writing.slug);
          setView("writing");
        } else {
          setView("about");
        }
      }, { disablePinnedHeader: true });
      if (!window.location.hash) {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const goBack = () => {
    startViewTransition(() => {
      if (view === "writing") {
        setView("writings");
        window.history.pushState(null, "", "/");
        return;
      }
      setView("about");
    }, { disablePinnedHeader: view === "writing" });
  };

  const openWriting = (slug: string) => {
    startViewTransition(() => {
      setSelectedWritingSlug(slug);
      setView("writing");
      window.history.pushState(null, "", writingPath(slug));
    }, { disablePinnedHeader: true });
    window.scrollTo(0, 0);
  };

  if (view === "writing") {
    return (
      <div className="app-container writing-page-container min-h-screen w-full overflow-x-hidden bg-background px-8 py-16 font-sans font-normal">
        <div className="relative flex w-full flex-col items-center">
          {writingToc.length > 0 ? (
            <WritingTableOfContents items={writingToc} />
          ) : null}

          <div className="writing-article-shell order-1 w-full max-w-[550px] px-6 text-left">
            <WritingArticle
              body={selectedWritingBody}
              headingIds={headingIds}
              lead={
                <>
                  <div className="writing-cover-top flex items-center justify-between gap-x-4 gap-y-2">
                    <button
                      type="button"
                      onClick={goBack}
                      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm text-tertiary transition-transform duration-150 hover:-translate-x-0.5"
                    >
                      <ChevronLeft aria-hidden="true" size={13} strokeWidth={2} />
                      Back
                    </button>

                    <button
                      type="button"
                      className={themeToggleClassName}
                      aria-label={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
                      aria-pressed={isDarkMode}
                      onClick={() => setIsDarkMode((currentMode) => !currentMode)}
                    />
                  </div>

                  <header className="writing-cover-header mt-6">
                    <h1 id="writing-title" className="writing-cover-title m-0 mt-2 text-xl font-medium text-primary">{selectedWriting.title}</h1>
                  </header>

                  {selectedWriting.description ? (
                    <p className="m-0 mt-4 text-sm text-secondary">{selectedWriting.description}</p>
                  ) : null}
                </>
              }
              leadFooter={
                <div className="writing-cover-meta flex flex-wrap items-center gap-2">
                  {selectedWriting.lastUpdated ? (
                    <p className="m-0 text-sm text-tertiary">Last updated {selectedWriting.lastUpdated}</p>
                  ) : (
                    <p className="m-0 text-sm text-tertiary">{selectedWriting.date}</p>
                  )}
                  {selectedWriting.status ? (
                    <span className={wipTagClassName}>{selectedWriting.status}</span>
                  ) : null}
                </div>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container flex min-h-screen w-full items-center justify-center overflow-hidden bg-background p-8 font-sans font-normal">
      <div className="content-section flex h-[326px] w-full max-w-[550px] flex-col text-left">
        <div className="header-row flex w-full items-center justify-between">
          <p className="m-0 text-sm font-medium leading-5 text-primary">
            Shun Ueno
          </p>
          <button
            type="button"
            className={themeToggleClassName}
            aria-label={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
            aria-pressed={isDarkMode}
            onClick={() => setIsDarkMode((currentMode) => !currentMode)}
          />
        </div>
        <p className="m-0 text-sm leading-[1.6] text-tertiary">
          <span className="subtitle-swap" key={view}>
            {view === "about" ? (
              "Head of Design"
            ) : (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 font-inherit text-inherit transition-transform duration-150 hover:-translate-x-0.5"
              >
                <ChevronLeft aria-hidden="true" size={13} strokeWidth={2} />
                Back
              </button>
            )}
          </span>
        </p>

        <div className="swap-panel flex-1">
          <div className="view-panel" key={view}>
            {view === "about" ? (
              <>
                <p className="content-paragraph m-0 mt-6 text-sm text-primary">
                  I currently design foundational systems and interfaces for self-improving agents at <ExternalLink href="https://judgmentlabs.ai">Judgment Labs</ExternalLink>. Previously, I studied Computer Science and <ExternalLink href="https://dschool.stanford.edu/">Product Design</ExternalLink> at Stanford University.
                </p>

                <p className="content-paragraph m-0 mt-4 text-sm text-primary">
                  I enjoy working on problems where the space is still undefined and the right approach isn&apos;t obvious.
                </p>

                <p className="content-paragraph m-0 mt-4 text-sm text-primary">
                  In my free time, I enjoy writing about my experiments, what I&apos;m learning as a founding designer, and how my role is evolving.
                </p>

                <p className="content-paragraph m-0 mt-4 text-sm text-primary">
                  If you have something interesting in mind,<span className="mobile-line-break"> <ExternalLink href="https://x.com/22uenos">get in touch</ExternalLink>.</span>
                </p>
              </>
            ) : (
              <>
                <p className="content-paragraph m-0 mt-6 text-sm text-primary">
                  You've found my notes! You are probably one of the few people who have haha. This is a collection of unfinished and unpolished writings and experiments. I treat this like my Notes app, just written in public.
                </p>
                <ul className="writings-list m-0 mt-8 list-none divide-y divide-subtle border-y border-subtle p-0">
                  {writings.map((w) => (
                    <li key={w.slug}>
                      <button
                        type="button"
                        onClick={() => openWriting(w.slug)}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 border-0 bg-transparent py-1.5 text-left font-inherit text-inherit transition-opacity duration-150 hover:opacity-65"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="text-sm leading-[1.6] text-primary">{w.title}</span>
                          {w.status ? <span className={wipTagClassName}>{w.status}</span> : null}
                        </span>
                        <span className="shrink-0 text-[13px] leading-[1.6] text-tertiary">{w.date}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
